import { createSlice, createAction } from '@reduxjs/toolkit';
import { XMLDataSlice, StoreVM, OptionProps } from '../view-models';
import { convertJsonPolicyToGraph } from '../xmlParsing/convertJsonPolicyToGraph';
import { OrchestrationStep, PolicyComponents, SubJourney, UserJourney } from '../xmlParsing/interfaces';
import constants from '../constants/constants';
import { cloneDeep } from 'lodash';
import { mergeJourneys, mergeTechnicalProfile, mergeObjects, getUpdatedTPData } from '../utils/mergeUtils';
import { getNestedValue } from '../utils/utils';

const initialState: XMLDataSlice = {
  hasError: true,
  nodes: [],
  edges: [],
  transformationConfig: {},
  policyComponents: {},
  rpComponents: {},
  rpInheritance: {},
  isDebugPolicy: false,
  debugPolicyLog: [],
  fileTraverseOrder: [],
  activePolicyFiles: [],
  journeyNameType: {
    name: '',
    type: 'UserJourneys',
  },
  searchResults: [],
  searchTPResults: {},
  selectedSearch: null,
};

const reactFlowSlice = createSlice({
  name: 'xmlData',
  initialState,
  reducers: {
    setReactFlowError: (state, { payload }) => {
      state.hasError = payload;
    },
    setComponentsOnUpload: (state, { payload }) => {
      state.policyComponents = payload['policyComponents'] ? payload['policyComponents'] : {};
      state.rpComponents = payload['rpComponents'] ? payload['rpComponents'] : {};
      state.rpInheritance = payload['rpInheritance'] ? payload['rpInheritance'] : {};
      state.fileTraverseOrder = [];
      state.activePolicyFiles = [];
      state.journeyNameType = {
        name: '',
        type: 'UserJourneys',
      };
      state.searchResults = [];
      state.selectedSearch = null;
    },
    addDuplicateState: (state) => {
      state.activePolicyFiles = state.activePolicyFiles.concat(state.activePolicyFiles.slice(-1));
    },
    replaceDuplicateState: (state) => {
      state.activePolicyFiles = state.activePolicyFiles.slice(0, -2).concat(state.activePolicyFiles.slice(-1));
      // update the policyComponents and RPComponents
      if (state.activePolicyFiles.length === 1) {
        const _updateJSON = cloneDeep(state.activePolicyFiles)[0];
        const _updateFiles = cloneDeep(state.fileTraverseOrder);
        while (_updateJSON.length > 1) {
          const _policyFile = _updateJSON.shift();
          const _fileName = _updateFiles.shift() || '';
          state.policyComponents[_fileName] = _policyFile;
        }
        state.rpComponents[_updateFiles[0]] = _updateJSON[0];
      }
    },
    discardDuplicateState: (state) => {
      // remove the last duplicate one
      state.activePolicyFiles.pop();
    },
    resetDuplicateState: (state) => {
      // remove the last one and get the original state when model opened
      state.activePolicyFiles = state.activePolicyFiles.slice(0, -1).concat(state.activePolicyFiles.slice(-2, -1));
    },
    setSearchResult: (state, { payload }) => {
      state.searchResults = payload || [];
    },
    setSearchTPResult: (state, { payload }) => {
      state.searchTPResults = payload || {};
    },
    setSelectedSearch: (state, { payload }) => {
      state.selectedSearch = payload || null;
    },
    setNodes: (state, { payload }) => {
      state.nodes = payload ? payload : [];
    },
    setEdges: (state, { payload }) => {
      state.edges = payload ? payload : [];
    },
    setTransformationConfig: (state, { payload }) => {
      state.transformationConfig = payload ? payload : {};
    },
    setFileTraverseOrder: (state, { payload }) => {
      state.fileTraverseOrder = payload ? payload : [];
      const _tempFileOrder = cloneDeep(state.fileTraverseOrder);
      const rpFileName: string = _tempFileOrder.pop() || '';

      const res = _tempFileOrder
        .reduce((acc: any[], fileName: string) => {
          return acc.concat([state.policyComponents[fileName]]);
        }, [])
        .concat([state.rpComponents[rpFileName]]);
      state.activePolicyFiles = [res];
    },
    deleteOperations: (state, { payload }) => {
      const _allStates = cloneDeep(state.activePolicyFiles);
      const _latestState = _allStates.pop();
      const _objName =
        payload['protocolName'] === constants.PROTOCOL_NAME.selfAssertedProfiles
          ? constants.PROTOCOL_NAME.selfAssertedProfiles
          : constants.PROTOCOL_NAME.otherProfile;

      _latestState.forEach((_file: any) => {
        const _res = getNestedValue(_file, _objName, payload['selectedTP'], payload['type']);
        if (_res) {
          let _ind = -1;
          switch (payload['type']) {
            case constants.INPUT_CLAIMS:
            case constants.OUTPUT_CLAIMS:
            case constants.DISPLAY_CLAIMS:
            case constants.PERSISTED_CLAIMS:
              _ind = _res.findIndex((el: any) => payload['id'] === el['claimTypeReferenceId']);
              break;
            case constants.INPUT_CLAIMS_TRANSFORMATIONS:
            case constants.OUTPUT_CLAIMS_TRANSFORMATIONS:
              _ind = _res.findIndex((el: any) => payload['id'] === el);
              break;
            case constants.VALIDATION_TECHNICAL_PROFILES:
              _ind = _res.findIndex((el: any) => payload['id'] === el['id']);
              break;
            default:
              window.alert('This operation is not defined');
              break;
          }
          _ind != -1 && _file[_objName][payload['selectedTP']][payload['type']].splice(_ind, 1);
        }
      });
      state.activePolicyFiles = _allStates.concat([_latestState]);
    },

    updatePrecondition: (state, { payload }) => {
      const _allStates = cloneDeep(state.activePolicyFiles);
      const _latestState = _allStates.pop();
      const _rpFile = _latestState.pop(); // removes the RPFile JSON
      const _fileToUpdate = _latestState.pop(); // removes the first next to RPFile
      const journeyType = payload['journeyType'] === 'SubJourneys' ? 'subJourneys' : 'userJourneys';
      const _mergedPolicy: any = mergeJourneys(cloneDeep(_latestState), journeyType);
      if (Object.keys(_mergedPolicy).length === 0) {
        // there is only base file and RP file in this userjourney
        // Update the base file
        const ujIndex: number = _fileToUpdate[journeyType].findIndex(
          (el: UserJourney) => el.id === payload['journeyName'],
        );
        if (ujIndex != -1) {
          const orchStepIndex: number = _fileToUpdate[journeyType][ujIndex]['orchestrationSteps'].findIndex(
            (step: OrchestrationStep) => step.order === payload['order'],
          );
          _fileToUpdate[journeyType][ujIndex]['orchestrationSteps'][orchStepIndex]['preconditions'] = payload['data'];
        } else {
          // This condition will never come
          window.alert('The USerjourney does not exist in the base file');
        }
        const finalUpdatedState = [..._latestState, _fileToUpdate, _rpFile];
        state.activePolicyFiles = _allStates.concat([finalUpdatedState]);
      } else {
        // there are more than 2 files
        // Get the index from the latest non RP file
        let _filejIndex: number = _fileToUpdate[journeyType].findIndex(
          (el: UserJourney | SubJourney) => el.id === payload['journeyName'],
        );
        // If the journey does not exists in latest non RP file, look for the journey from the merged list.
        if (_filejIndex === -1) {
          _filejIndex = _mergedPolicy[journeyType].findIndex(
            (el: UserJourney | SubJourney) => el.id === payload['journeyName'],
          );
          // The above step should definitely have an index;
          if (_filejIndex === -1) {
            window.alert('This scenario will never come.');
          }
          let _ujTemplate: UserJourney = _mergedPolicy[journeyType][_filejIndex];
          // userJourney / subJourney ID exists in the _ujTemplate
          const orchStepUpdataIndex: number = _ujTemplate['orchestrationSteps'].findIndex(
            (step: OrchestrationStep) => step.order === payload['order'],
          );
          if (orchStepUpdataIndex != -1) {
            let _stepToUpdate: OrchestrationStep =
              _mergedPolicy[journeyType][_filejIndex]['orchestrationSteps'][orchStepUpdataIndex];
            _stepToUpdate['preconditions'] = payload['data'];

            // The userJourney / subJourney's orch step's precondition is updated.
            _ujTemplate['orchestrationSteps'] = [_stepToUpdate];
          } else {
            window.alert('This sceanrio will never come');
          }
          let updated: boolean = false;
          _fileToUpdate[journeyType] = _fileToUpdate[journeyType].reduce(
            (acc: any[], journey: UserJourney | SubJourney) => {
              if (journey.id === _ujTemplate.id) {
                updated = true;
                return acc.concat(_ujTemplate);
              } else {
                return acc.concat(journey);
              }
            },
            [],
          );
          if (!updated) {
            _fileToUpdate[journeyType].push(_ujTemplate);
          }
        } else {
          // userJourney / subJourney ID exists in the first non RP file
          const orchStepUpdateIndex: number = _fileToUpdate[journeyType][_filejIndex]['orchestrationSteps'].findIndex(
            (step: OrchestrationStep) => step.order === payload['order'],
          );

          if (orchStepUpdateIndex != -1) {
            // The journey has the order to be updated.
            _fileToUpdate[journeyType][_filejIndex]['orchestrationSteps'][orchStepUpdateIndex]['preconditions'] =
              payload['data'];
          } else {
            // Need to fetch the orch step from the merged parent files.
            // Since it was not there in the first nonRP file, the journey Id with the right orch step order should exist in the merged file
            const _tempIndex = _mergedPolicy[journeyType].findIndex(
              (el: UserJourney | SubJourney) => el.id === payload['journeyName'],
            );
            const _orchStepUpdateIndex: number = _mergedPolicy[journeyType][_tempIndex]['orchestrationSteps'].findIndex(
              (step: OrchestrationStep) => step.order === payload['order'],
            );

            let _stepToUpdate: OrchestrationStep =
              _mergedPolicy[journeyType][_tempIndex]['orchestrationSteps'][_orchStepUpdateIndex];
            _stepToUpdate['preconditions'] = payload['data'];
            _fileToUpdate[journeyType][_filejIndex]['orchestrationSteps'].push(_stepToUpdate);
          }
        }
        const finalUpdatedState = [..._latestState, _fileToUpdate, _rpFile];
        state.activePolicyFiles = _allStates.concat([finalUpdatedState]);
      }
    },

    updateJourney: (state, { payload }) => {
      const _allStates = cloneDeep(state.activePolicyFiles);
      const _latestState = _allStates.pop();
      const _journeyType = payload['journeyNameType']['type'] === 'SubJourneys' ? 'subJourneys' : 'userJourneys';
      // @ts-ignore
      const _journeys = mergeJourneys(cloneDeep(_latestState), _journeyType)[_journeyType];
      const _mergedUJindex = _journeys.findIndex(
        (el: UserJourney | SubJourney) => el.id === payload['journeyNameType']['name'],
      );
      let _orchStep: OrchestrationStep =
        payload['position'] === 'after'
          ? { ...payload['orchStep'], order: payload['orchStep']['order'] + 1 }
          : payload['orchStep'];

      // List that would store all the step orders that needs to be updated.
      const orderToUpdate: number[] = [];
      _journeys[_mergedUJindex]['orchestrationSteps'].forEach((step: any) => {
        if (step['order'] === payload['orchStep']['order'] && payload['position'] === 'before') {
          orderToUpdate.push(step['order']);
        } else if (step['order'] > payload['orchStep']['order']) {
          orderToUpdate.push(step['order']);
        }
      });

      // Update the order from all the files which are there in orderToUpdate
      _latestState.forEach((file: PolicyComponents, index: number) => {
        // @ts-ignore
        const _updateUJOrderIndex = file[_journeyType].findIndex(
          (uj: UserJourney | SubJourney) => uj['id'] === payload['journeyNameType']['name'],
        );
        if (_updateUJOrderIndex !== -1) {
          // @ts-ignore
          file[_journeyType][_updateUJOrderIndex]['orchestrationSteps'].forEach(
            (step: OrchestrationStep, _ind: number) => {
              if (orderToUpdate.includes(step['order'])) {
                _latestState[index][_journeyType][_updateUJOrderIndex]['orchestrationSteps'][_ind] = {
                  ...step,
                  order: step['order'] + 1,
                };
              }
            },
          );
        }
      });

      // Add the orchestration step to the userJourney / subJourney in the last extension file.
      const _rpFile = _latestState.pop(); // removes the RPFile JSON
      const _fileToUpdate = _latestState.pop(); // removes the first next to RPFile
      const _updateUJStepIndex = _fileToUpdate[_journeyType].findIndex(
        (uj: UserJourney) => uj['id'] === payload['journeyNameType']['name'],
      );
      if (_updateUJStepIndex === -1) {
        // Create new userjourney
        _fileToUpdate[_journeyType].push({
          ..._journeys[_mergedUJindex],
          orchestrationSteps: [_orchStep],
        });
      } else {
        // Add the orchestration step alone
        _fileToUpdate[_journeyType][_updateUJStepIndex]['orchestrationSteps'].push(_orchStep);
        // Sort the orchestration step based on the order number.
        _fileToUpdate[_journeyType][_updateUJStepIndex]['orchestrationSteps'].sort(
          (a: OrchestrationStep, b: OrchestrationStep) => a['order'] - b['order'],
        );
      }

      const finalUpdatedState = [..._latestState, _fileToUpdate, _rpFile];
      state.activePolicyFiles = _allStates.concat([finalUpdatedState]);
    },
    // add claims to the first extension file
    addClaimsList: (state, { payload }) => {
      const _allStates = cloneDeep(state.activePolicyFiles);
      const _latestState = _allStates.pop();
      const _rpFile = _latestState.pop(); // removes the RPFile JSON
      const _fileToUpdate = _latestState.pop(); // removes the first next to RPFile
      if (payload['userInputType'] === 'Textbox') delete payload['restriction'];
      _fileToUpdate['claims'][payload['id']] = payload;
      const finalUpdatedState = [..._latestState, _fileToUpdate, _rpFile];
      state.activePolicyFiles = _allStates.concat([finalUpdatedState]);
    },
    // add/update claimTransformations to the first extension file
    addUpdateClaimTransformationsList: (state, { payload }) => {
      const _allStates = cloneDeep(state.activePolicyFiles);
      const _latestState = _allStates.pop();
      const _rpFile = _latestState.pop(); // removes the RPFile JSON
      const _fileToUpdate = _latestState.pop(); // removes the first next to RPFile
      _fileToUpdate['claimTransformations'][payload['id']] = payload;
      const finalUpdatedState = [..._latestState, _fileToUpdate, _rpFile];
      state.activePolicyFiles = _allStates.concat([finalUpdatedState]);
    },
    // addOtherProfilesList: (state, { payload }) => {
    //   state.rpComponents[state.fileTraverseOrder[state.fileTraverseOrder.length - 1]].otherProfiles = {
    //     ...state.rpComponents[state.fileTraverseOrder[state.fileTraverseOrder.length - 1]].otherProfiles,
    //     [payload.id]: payload,
    //   };
    // },
    replaceTechnicalProfile: (state, { payload }) => {
      const _allStates = cloneDeep(state.activePolicyFiles);
      const _latestState = _allStates.pop();
      for (let i = _latestState.length - 1; i >= 0; i--) {
        if (_latestState[i][payload['parentTPType']].hasOwnProperty(payload['parentTP'])) {
          const _index = _latestState[i][payload['parentTPType']][payload['parentTP']][payload['type']].findIndex(
            (el: any) => el.id === payload['oldId'],
          );
          if (_index != -1) {
            _latestState[i][payload['parentTPType']][payload['parentTP']][payload['type']][_index]['id'] =
              payload['data']['id'];
            break;
          }
        }
      }

      const _rpFile = _latestState.pop(); // removes the RPFile JSON
      const _objName =
        payload['protocolName'] === constants.PROTOCOL_NAME.selfAssertedProfiles
          ? constants.PROTOCOL_NAME.selfAssertedProfiles
          : constants.PROTOCOL_NAME.otherProfile;
      let finalUpdatedState;
      if (_rpFile.hasOwnProperty(_objName) && _rpFile[_objName].hasOwnProperty(payload['data']['id'])) {
        // update the RP File
        _rpFile[_objName][payload['data']['id']] = payload['data'];
        finalUpdatedState = [..._latestState, _rpFile];
      } else {
        // update the file next to RP File
        const _fileToUpdate = _latestState.pop(); // removes the first next to RPFile
        _fileToUpdate[_objName][payload['data']['id']] = payload['data'];
        finalUpdatedState = [..._latestState, _fileToUpdate, _rpFile];
      }
      state.activePolicyFiles = _allStates.concat([finalUpdatedState]);
    },
    addTechnicalProfile: (state, { payload }) => {
      const _allStates = cloneDeep(state.activePolicyFiles);
      const _latestState = _allStates.pop();
      const _rpFile = _latestState.pop(); // removes the RPFile JSON
      const _objName =
        payload['protocolName'] === constants.PROTOCOL_NAME.selfAssertedProfiles
          ? constants.PROTOCOL_NAME.selfAssertedProfiles
          : constants.PROTOCOL_NAME.otherProfile;
      let finalUpdatedState;
      if (_rpFile.hasOwnProperty(_objName) && _rpFile[_objName].hasOwnProperty(payload['data']['id'])) {
        // update the RP File
        _rpFile[_objName][payload['data']['id']] = payload['data'];
        finalUpdatedState = [..._latestState, _rpFile];
      } else {
        // update the file next to RP File
        const _fileToUpdate = _latestState.pop(); // removes the first next to RPFile
        _fileToUpdate[_objName][payload['data']['id']] = payload['data'];
        finalUpdatedState = [..._latestState, _fileToUpdate, _rpFile];
      }
      state.activePolicyFiles = _allStates.concat([finalUpdatedState]);
    },
    updateTechnicalProfile: (state, { payload }) => {
      const _allStates = cloneDeep(state.activePolicyFiles);
      const _latestState = _allStates.pop();
      const _rpFile = _latestState.pop(); // removes the RPFile JSON
      const _objName =
        payload['protocolName'] === constants.PROTOCOL_NAME.selfAssertedProfiles
          ? constants.PROTOCOL_NAME.selfAssertedProfiles
          : constants.PROTOCOL_NAME.otherProfile;
      let _mergedPolicy: any;
      if (_rpFile.hasOwnProperty(_objName) && _rpFile[_objName].hasOwnProperty(payload['selectedTP'])) {
        // rpFile has the selected profiles
        _mergedPolicy = mergeTechnicalProfile(_latestState, _objName);
        _rpFile[_objName][payload['selectedTP']] = getUpdatedTPData(
          _rpFile[_objName][payload['selectedTP']],
          _mergedPolicy[_objName][payload['selectedTP']],
          _mergedPolicy['claims'],
          payload,
        );
        const finalUpdatedState = [..._latestState, _rpFile];
        state.activePolicyFiles = _allStates.concat([finalUpdatedState]);
      } else {
        const _fileToUpdate = _latestState.pop(); // removes the first next to RPFile
        _mergedPolicy = mergeTechnicalProfile(cloneDeep(_latestState), _objName);
        if (Object.keys(_mergedPolicy).length === 0) {
          // there is only base file and RP file in this userjourney
          // Update the base file
          _fileToUpdate[_objName][payload['selectedTP']] = getUpdatedTPData(
            _fileToUpdate[_objName][payload['selectedTP']],
            {},
            _mergedPolicy['claims'],
            payload,
          );
        } else {
          // there are more than 2 files
          // @ts-ignore
          if (_mergedPolicy[_objName].hasOwnProperty(payload['selectedTP'])) {
            // Technical profile is the outcome of one or more files
            _fileToUpdate[_objName][payload['selectedTP']] = getUpdatedTPData(
              _fileToUpdate[_objName][payload['selectedTP']],
              // @ts-ignore
              _mergedPolicy[_objName][payload['selectedTP']],
              _mergedPolicy['claims'],
              payload,
            );
          } else {
            // Technical profile does not exist in any of the parent files.
            _fileToUpdate[_objName][payload['selectedTP']] = getUpdatedTPData(
              _fileToUpdate[_objName][payload['selectedTP']],
              {},
              _mergedPolicy['claims'],
              payload,
            );
          }
        }
        const finalUpdatedState = [..._latestState, _fileToUpdate, _rpFile];
        state.activePolicyFiles = _allStates.concat([finalUpdatedState]);
      }

      // const _fileTraverse = JSON.parse(JSON.stringify(state.fileTraverseOrder)).reverse();
      // const rpFile = _fileTraverse.shift();
      // if (state.rpComponents[rpFile].selfAssertedProfiles.hasOwnProperty(payload['selectedTP'])) {
      //   // TP is defined in RP File
      //   state.rpComponents[rpFile].selfAssertedProfiles[payload['selectedTP']] = payload.data;
      // } else {
      //   // TP is defined in extension / base file
      //   // Update the first non RP file.
      //   state.policyComponents[_fileTraverse[0]].selfAssertedProfiles[payload['selectedTP']] = payload.data;
      // }
    },
    setPolicyComponents: (state, { payload }) => {
      state.policyComponents = payload ? payload : {};
      state.fileTraverseOrder = [];
    },
    setRPComponents: (state, { payload }) => {
      state.rpComponents = payload ? payload : {};
    },
    setRPInheritance: (state, { payload }) => {
      state.rpInheritance = payload ? payload : {};
    },
    setNodesAndEdges: (state, { payload }) => {
      if (state.fileTraverseOrder.length === 0) {
        state.nodes = [];
        state.edges = [];
        state.isDebugPolicy = false;
      } else {
        if (state.activePolicyFiles.length === 1) {
          const _latestState = cloneDeep(state.activePolicyFiles).slice(-1)[0];
          const _journeyType = payload['type'] == 'UserJourneys' ? 'userJourneys' : 'subJourneys';
          // @ts-ignore
          const journeys = mergeJourneys(cloneDeep(_latestState), _journeyType)[_journeyType];
          const jIndex = journeys.findIndex((el: UserJourney) => el.id === payload['name']);
          // If the journeyIndex does not exist, there is no point in getting the JSON.
          if (jIndex === -1) return;
          const { nodes, edges, isDebugPolicy } = convertJsonPolicyToGraph(journeys, jIndex);
          state.nodes = nodes;
          state.edges = edges;
          state.isDebugPolicy = isDebugPolicy;
        }
      }
    },
    setIsDebugPolicy: (state, { payload }) => {
      state.isDebugPolicy = payload;
    },
    setDebugPolicyLog: (state, { payload }) => {
      state.debugPolicyLog = payload;
    },
    setJourneyNameType: (state, { payload }) => {
      state.journeyNameType = payload;
    },
  },
});

export const loadTranformationConfig = createAction('xmlData/loadTranformationConfig');
export const isReactFlowError = (state: StoreVM) => state.xmlData.hasError;
export const getNodes = (state: StoreVM) => state.xmlData.nodes;
export const getEdges = (state: StoreVM) => state.xmlData.edges;
export const getFileTraverseOrder = (state: StoreVM) => state.xmlData.fileTraverseOrder;
export const getIsDebugPolicy = (state: StoreVM) => state.xmlData.isDebugPolicy;
export const getDebugPolicyLog = (state: StoreVM) => state.xmlData.debugPolicyLog;
export const getTransformationConfig = (state: StoreVM) => state.xmlData.transformationConfig;
export const getPolicyComponents = (state: StoreVM) => state.xmlData.policyComponents;
export const getRPComponents = (state: StoreVM) => state.xmlData.rpComponents;
export const getRPInheritance = (state: StoreVM) => state.xmlData.rpInheritance;
export const getJourneyNameType = (state: StoreVM) => state.xmlData.journeyNameType;
export const getLatestFiles = (state: StoreVM) => cloneDeep(state.xmlData.activePolicyFiles).slice(-1)[0];
export const getSearchResult = (state: StoreVM) => state.xmlData.searchResults;
export const getSearchTPResult = (state: StoreVM) => state.xmlData.searchTPResults;
export const getSelectedSearch = (state: StoreVM) => state.xmlData.selectedSearch;

export const getClaims = (state: StoreVM) =>
  // @ts-ignore
  state.xmlData.fileTraverseOrder.length === 0 ? {} : mergeObjects(getLatestFiles(state), 'claims')['claims'];

export const getClaimsDropdownList = (state: StoreVM) =>
  state.xmlData.fileTraverseOrder.length === 0
    ? []
    : // @ts-ignore
      Object.keys(mergeObjects(getLatestFiles(state), 'claims')['claims']).reduce(
        (acc: OptionProps[], curVal: string) => acc.concat([{ label: curVal, value: curVal }]),
        [],
      );

export const getClaimTransformations = (state: StoreVM) =>
  state.xmlData.fileTraverseOrder.length === 0
    ? {}
    : // @ts-ignore
      mergeObjects(getLatestFiles(state), 'claimTransformations')['claimTransformations'];

export const getClaimTransformationsDropdownList = (state: StoreVM) =>
  state.xmlData.fileTraverseOrder.length === 0
    ? []
    : // @ts-ignore
      Object.keys(mergeObjects(getLatestFiles(state), 'claimTransformations')['claimTransformations']).reduce(
        (acc: OptionProps[], curVal: string) => acc.concat([{ label: curVal, value: curVal }]),
        [],
      );

export const getUserJourneys = (state: StoreVM) =>
  state.xmlData.fileTraverseOrder.length === 0
    ? []
    : // @ts-ignore
      mergeJourneys(cloneDeep(getLatestFiles(state)), 'userJourneys')['userJourneys'];

export const getSubJourneys = (state: StoreVM) =>
  state.xmlData.fileTraverseOrder.length === 0
    ? []
    : // @ts-ignore
      mergeJourneys(cloneDeep(getLatestFiles(state)), 'subJourneys')['subJourneys'];

export const getSelfAssertedProfiles = (state: StoreVM) =>
  state.xmlData.fileTraverseOrder.length === 0
    ? {}
    : // @ts-ignore
      mergeTechnicalProfile(getLatestFiles(state), 'selfAssertedProfiles')['selfAssertedProfiles'];

export const getOtherProfiles = (state: StoreVM) =>
  state.xmlData.fileTraverseOrder.length === 0
    ? {}
    : // @ts-ignore
      mergeTechnicalProfile(getLatestFiles(state), 'otherProfiles')['otherProfiles'];

export const getOtherProfilesDropdownList = (state: StoreVM) =>
  state.xmlData.fileTraverseOrder.length === 0
    ? []
    : // @ts-ignore
      Object.keys(mergeTechnicalProfile(getLatestFiles(state), 'otherProfiles')['otherProfiles']).reduce(
        (acc: OptionProps[], curVal: string) => acc.concat([{ label: curVal, value: curVal }]),
        [],
      );

export const {
  setReactFlowError,
  setComponentsOnUpload,
  addDuplicateState,
  replaceDuplicateState,
  discardDuplicateState,
  resetDuplicateState,
  setNodes,
  setEdges,
  setTransformationConfig,
  setFileTraverseOrder,
  deleteOperations,
  updateJourney,
  updatePrecondition,
  replaceTechnicalProfile,
  addTechnicalProfile,
  updateTechnicalProfile,
  setPolicyComponents,
  setRPComponents,
  setRPInheritance,
  setNodesAndEdges,
  setIsDebugPolicy,
  setDebugPolicyLog,
  addClaimsList,
  addUpdateClaimTransformationsList,
  setJourneyNameType,
  setSearchResult,
  setSearchTPResult,
  setSelectedSearch,
} = reactFlowSlice.actions;

export const { reducer: xmlDataReducer } = reactFlowSlice;
